import React, { createContext, ReactChild } from "react";
import { ProductBuilderData, ProductBuilderOptions } from "../types/ProductBuilderTypes";
import {
  CloudinaryImage,
  ContentfulCloudinaryImageComponent,
  ContentfulPageProductPageV2,
  ContentfulSocialCurationsData,
  ReferentialImage,
  ResolvedTab,
  SiteflowResponse,
} from "../types/ContentfulTypes";
import { PageContext } from "../types/Global";
import { CartTenantDetails } from "@vp/cart-client";
import { CtaOverrideEntries } from "../build/product-builder-ctas/ctaOverrideTypes";
import { MerchandisingProductView } from "../types/ContentfulTypes";
import { RatingReviewData } from "@vp/rating-reviews-component";
import { createAllTabs } from "../utils/Tabs";
import { CloudinaryVideoMedia } from "../types/CloudinaryTypes";
import { ComponentData } from "../build/PageContextBuilder";

export interface PageStaticDataContextType {
  isPreview: boolean;
  locale: string;
  country: string;
  pageId: string;
  spaceId: string;
  productBuilderOptions: ProductBuilderOptions;
  referentialImages: ReferentialImage[];
  placeHolderImage: string;
  vistacartTenant: CartTenantDetails;
  decision?: string;
  canonicalDomain: string;
  trackingConfiguration: {
    pageSection: string;
    pageStage: string;
    pageName: string;
  };
  ctaOverrideEntries: CtaOverrideEntries;
  initialBuildState: {
    siteflow?:
      | {
          flows: SiteflowResponse[];
        }
      | undefined;
  };
  ssrQuantities: number[];
  mpv: MerchandisingProductView;
  contentfulName: string;
  productBuilder: ProductBuilderData;
  heroGallery: (ContentfulCloudinaryImageComponent<CloudinaryImage> | CloudinaryVideoMedia)[];
  breadcrumbs: { url: string; name: string }[];
  description: string;
  tlpUrl?: string;
  removeShippingNotIncludedCallout?: boolean;
  enableFscLogo?: boolean;
  socialCurationsTitle: string;
  socialCurationsSubtitle: string;
  socialCurations: ContentfulSocialCurationsData;
  enableCampaignCallouts?: boolean;
  hideReviews?: boolean;
  reviews: RatingReviewData;
  tabData: ResolvedTab[];
  isSSR: boolean;
  tenant: string;
  requestor: string;
  isDevelopment: boolean;
  componentData: ComponentData;
}

export const defaultContext: PageStaticDataContextType = {
  isPreview: false,
  locale: "",
  country: "",
  pageId: "",
  spaceId: "",
  productBuilderOptions: {
    selections: [],
  },
  referentialImages: [],
  placeHolderImage: "",
  vistacartTenant: {
    tenantId: "",
    cartUrl: "",
    checkoutUrl: "",
    absoluteCartUrl: "",
    absoluteCheckoutUrl: "",
    siteName: "",
    absoluteOrderDetailsUrl: "",
    termsAndConditions: "",
    privacyPolicy: "",
    paymentEnvironment: "",
    siteCountry: "",
    allowedBillingCountries: [],
    allowedShippingCountries: [],
    language: "",
    customerCareContact: "",
  },
  decision: "",
  canonicalDomain: "",
  trackingConfiguration: {
    pageSection: "",
    pageStage: "",
    pageName: "",
  },
  ctaOverrideEntries: {},
  ssrQuantities: [],
  mpv: {
    mpvId: "",
    version: 0,
    name: "",
    coreProductId: "",
    externallyMarketable: false,
    merchandisingDetails: [],
    quantities: {},
    defaultProductDescription: "",
    defaultProductImage: {
      cloudinaryImage: {
        cloudinaryUrl: "",
      },
    },
    defaultPreselectedOptions: [],
  },
  contentfulName: "",
  heroGallery: [],
  productBuilder: {
    builderType: {
      builderType: "on-page",
      options: {},
    },
    useQuantityDropdown: false,
  },
  breadcrumbs: [],
  description: "",
  tlpUrl: "",
  removeShippingNotIncludedCallout: false,
  enableFscLogo: false,
  socialCurationsTitle: "",
  socialCurationsSubtitle: "",
  socialCurations: {
    apiKey: "",
    locale: "",
    source: {
      id: "",
      tag: "",
      type: "",
    },
    widget: {
      instanceId: "",
      jsId: "",
    },
  },
  enableCampaignCallouts: false,
  hideReviews: false,
  reviews: {
    tenant: "",
    locale: "",
    mpvId: "",
    sortBy: "",
    summary: {
      reviewsCount: 0,
      ratedXOutOfYLabel: "",
      averageRating: 0,
      reviewsCountLabel: "",
      reviewedByNCustomersLabel: "",
    },
    texts: {
      snapshotLabel: "",
      byLabel: "",
      authorSubmissionDateLabel: "",
      authorLocationLabel: "",
      merchantResponseLabel: "",
      displayingReviewsLabel: "",
      sortByLabel: "",
      sorting: {},
      detailedRating: {
        lessDetails: "",
        moreDetails: "",
      },
      disclosureCodeLabels: {},
    },
  },
  initialBuildState: {},
  tabData: [],
  isSSR: false,
  tenant: "",
  requestor: "",
  isDevelopment: false,
  componentData: {
    overviewData: null,
  },
};

export const PageStaticDataContext = createContext<PageStaticDataContextType>(defaultContext);

export interface PageStaticDataContextProviderProps {
  pageContext: PageContext;
  pageContentfulProductPageV2: ContentfulPageProductPageV2;
  vistacartTenant: CartTenantDetails;
  children?: ReactChild | ReactChild[] | undefined | null;
}

export const PageStaticDataContextProvider = (props: PageStaticDataContextProviderProps) => {
  const { pageContext, pageContentfulProductPageV2, vistacartTenant, children } = props;
  const tabData = createAllTabs({
    globalTabs: pageContentfulProductPageV2.globalTabs,
    sourceTabs: pageContentfulProductPageV2.resolvedTabs,
    placeHolderImage: pageContext.placeHolderImage ?? "",
    hideReviews: pageContentfulProductPageV2.hideReviews,
    editorOptionsTab: pageContext.editorOptionsTab,
  });

  const isSSR = typeof window === "undefined";
  const tenant = process.env.GATSBY_TENANT || "vistaprint";
  const requestor = isSSR ? "product-page-v2-build" : "product-page-v2";
  const isDevelopment = process.env.NODE_ENV === "development";

  const pageStaticData: PageStaticDataContextType = {
    isPreview: pageContext.isPreview,
    locale: pageContext.locale,
    country: pageContext.country,
    pageId: pageContext.pageId,
    spaceId: pageContext.spaceId,
    productBuilderOptions: pageContext.productBuilderOptions,
    referentialImages: pageContentfulProductPageV2?.referentialImages?.referentialImages,
    placeHolderImage: pageContext.placeHolderImage,
    vistacartTenant,
    decision: pageContext.decision,
    canonicalDomain: pageContext.canonicalDomain,
    trackingConfiguration: pageContext.trackingConfiguration,
    ctaOverrideEntries: pageContext.ctaOverrideEntries,
    initialBuildState: {
      siteflow: pageContext.ssrCtas,
    },
    ssrQuantities: pageContext.ssrQuantities,
    mpv: pageContentfulProductPageV2.mpv,
    contentfulName: pageContentfulProductPageV2.contentfulName,
    productBuilder: pageContentfulProductPageV2.productBuilder,
    heroGallery: pageContentfulProductPageV2.heroGallery,
    breadcrumbs: pageContext.breadcrumbs,
    description: pageContentfulProductPageV2.description,
    tlpUrl: pageContentfulProductPageV2.tlpUrl,
    removeShippingNotIncludedCallout: pageContentfulProductPageV2.removeShippingNotIncludedCallout,
    enableFscLogo: pageContentfulProductPageV2.enableFscLogo,
    socialCurationsTitle: pageContentfulProductPageV2.socialCurationsTitle,
    socialCurationsSubtitle: pageContentfulProductPageV2.socialCurationsSubtitle,
    socialCurations: pageContentfulProductPageV2.socialCurations,
    enableCampaignCallouts: pageContentfulProductPageV2.enableCampaignCallouts,
    hideReviews: pageContentfulProductPageV2.hideReviews,
    reviews: pageContext.reviews!,
    tabData,
    isSSR,
    tenant,
    requestor,
    isDevelopment,
    componentData: pageContext.componentData,
  };

  return (
    <PageStaticDataContext.Provider value={pageStaticData}>
      {children}
    </PageStaticDataContext.Provider>
  );
};
